import { Alert, Platform } from 'react-native';

/**
 * PREGUNTAR Y AVISAR SIN QUE LA ACCIÓN SE PIERDA EN EL NAVEGADOR.
 *
 * ═══ POR QUÉ EXISTE ═══════════════════════════════════════════════════════
 *
 * En react-native-web `Alert` es, completo:
 *
 *     class Alert { static alert() {} }
 *
 * Una función vacía. Así que en el navegador un `Alert.alert` con botones no
 * dibuja nada Y —lo grave— **el `onPress` del botón nunca se ejecuta**. No es
 * que se vea feo: la acción se pierde en silencio, sin error, sin registro.
 *
 * Lo destapó el botón de SOS (map.tsx): la llamada al 911 y el registro de la
 * alerta colgaban del `onPress`, así que en web se pulsaba el botón de pánico
 * y no pasaba absolutamente nada.
 *
 * Al barrer el repo aparecieron más de treinta sitios con el mismo patrón, y
 * entre ellos cosas que son dinero: subir el comprobante de recarga, cancelar
 * un pedido (que es el reembolso de Stripe), borrar una cuenta de banco.
 *
 * ═══ POR QUÉ UN ARCHIVO Y NO OTRA COPIA ══════════════════════════════════
 *
 * La solución ya estaba inventada... ocho veces. `window.confirm` a mano en
 * map.tsx (dos), dashboard.tsx (dos), profile.tsx, mis-vehiculos.tsx,
 * logistica-trabajos.tsx, logistica-mis-pedidos.tsx (tres),
 * restaurant-registration.tsx, DesktopSidebarLayout.tsx y modoInvitado.ts.
 *
 * Copiada ocho veces quiere decir que la novena pantalla se va a olvidar — y
 * eso es exactamente lo que pasó con el SOS. Aquí vive una sola vez.
 *
 * ═══ LO QUE NO HACE ══════════════════════════════════════════════════════
 *
 * No intenta ser bonito. `window.confirm` es el diálogo del navegador, feo y
 * cuadrado, pero sale SIEMPRE y devuelve una respuesta de verdad. Para una
 * decisión que mueve dinero o borra algo, eso vale más que el diseño.
 */

/**
 * Pregunta sí o no. Devuelve `true` solo si la persona aceptó.
 *
 * Cerrar el diálogo sin elegir cuenta como NO: nunca se da por aceptada una
 * acción que nadie confirmó.
 */
export function preguntarSiNo(
  titulo: string,
  mensaje: string,
  textoNo: string = 'Cancelar',
  textoSi: string = 'Continuar'
): Promise<boolean> {
  return new Promise((resolver) => {
    if (Platform.OS === 'web') {
      // `typeof window` por el render del servidor: en el export estático esto
      // corre una vez sin navegador, y sin la comprobación revienta la página.
      resolver(typeof window !== 'undefined' && window.confirm(`${titulo}\n\n${mensaje}`));
      return;
    }
    Alert.alert(
      titulo,
      mensaje,
      [
        { text: textoNo, style: 'cancel', onPress: () => resolver(false) },
        { text: textoSi, onPress: () => resolver(true) },
      ],
      { cancelable: true, onDismiss: () => resolver(false) }
    );
  });
}

/**
 * Lo mismo pero para algo grave (borrar, cancelar un pedido): en nativo el
 * botón sale en rojo. En web no hay forma de pintarlo, así que el peso lo
 * tiene que llevar el TEXTO — escribe un mensaje que no deje duda.
 */
export function preguntarAntesDeAlgoGrave(
  titulo: string,
  mensaje: string,
  textoNo: string = 'Cancelar',
  textoSi: string = 'Sí, continuar'
): Promise<boolean> {
  return new Promise((resolver) => {
    if (Platform.OS === 'web') {
      resolver(typeof window !== 'undefined' && window.confirm(`${titulo}\n\n${mensaje}`));
      return;
    }
    Alert.alert(
      titulo,
      mensaje,
      [
        { text: textoNo, style: 'cancel', onPress: () => resolver(false) },
        { text: textoSi, style: 'destructive', onPress: () => resolver(true) },
      ],
      { cancelable: true, onDismiss: () => resolver(false) }
    );
  });
}

/**
 * Avisar de algo, sin preguntar nada.
 *
 * Un `Alert.alert` informativo en web tampoco se ve, pero ahí no se pierde
 * ninguna acción: solo el mensaje. Aun así conviene que se vea, porque muchos
 * de esos mensajes son la única explicación de por qué algo no funcionó.
 */
export function avisar(titulo: string, mensaje: string): void {
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined') window.alert(`${titulo}\n\n${mensaje}`);
    return;
  }
  Alert.alert(titulo, mensaje);
}
